import { useWindowWidth } from '@react-hook/window-size'
import React, { useState } from 'react'
import { Box, Button, Flex, Heading, Link, Text, Image } from 'rebass'
import { trackHandler, useSupportedBrowser } from 'uneeq-react-core'
import { UnsupportedBrowser } from 'uneeq-react-ui'
import togglePrivacyIcon from '../assets/img/toggle_privacy_icon.svg'
import togglePrivacyIconOpen from '../assets/img/toggle_privacy_icon_open.svg'
import { isIOS } from 'react-device-detect'
import { isMobile, isTablet, isDesktop } from 'react-device-detect'
import config from '../../config'
import styles from './styles'
import { useIsSmallScreen } from 'uneeq-react-core'
import { Trans, useTranslation } from 'react-i18next'

import { default as dhImage } from '../assets/img/tess-full.png'

interface HomeProps {
  startSession: (token: string) => void
}

const Home: React.FC<HomeProps> = ({ startSession }) => {
  const { isBrowserSupported } = useSupportedBrowser()
  const [showPasscode, setShowPasscode] = useState(false)
  const isSmallScreen = useIsSmallScreen()
  const { t } = useTranslation()

  const [showPrivacySummary, setShowPrivacySummary] = useState(
    isDesktop || isTablet
  )

  const Paragraphs = () => {
    const togglePrivacySummary = () =>
      setShowPrivacySummary(!showPrivacySummary)

    const { t } = useTranslation()
    const avDataType = isIOS
      ? t('app:AvDataType.videoAndAudio')
      : t('app:AvDataType.audio')
    return (
      <>
        <Text
          as="p"
          sx={styles.privacyTextToggle}
          onClick={togglePrivacySummary}
        >
          <Trans i18nKey={`app:Home.privacyShort`} />
          <Image
            sx={styles.togglePrivacyIcon}
            src={showPrivacySummary ? togglePrivacyIconOpen : togglePrivacyIcon}
          />
        </Text>

        <Flex
          sx={{
            ...styles.paragraphContainer,
            maxHeight: showPrivacySummary ? 600 : 0,
            overflow:
              showPrivacySummary && !isDesktop && !isTablet
                ? 'visible'
                : 'hidden'
          }}
        >
          <Text as="p">
            <Trans
              i18nKey={`app:Home.privacy`}
              values={{ avatarName: config.avatarName, avDataType }}
              components={[
                <Link
                  href={config.privacyPolicyUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                />
              ]}
            />
          </Text>
        </Flex>
      </>
    )
  }

  if (showPasscode && !isBrowserSupported) {
    return <UnsupportedBrowser close={() => setShowPasscode(false)} />
  }

  const LetsChatButton = ({ sx }: any) => (
    <Flex
      sx={{
        ...styles.letsChatButtonContainer,
        justifyContent:
          showPrivacySummary && !isDesktop && !isTablet
            ? 'flex-start'
            : ['center', 'center', 'center', 'center', 'flex-start'],
        position:
          showPrivacySummary && !isDesktop && !isTablet
            ? 'relative'
            : ['absolute', 'absolute', 'absolute', 'absolute', 'relative'],
        bottom:
          showPrivacySummary && !isDesktop && !isTablet ? 0 : [90, 90, 0, 0],
        '@media only screen and (orientation: landscape)': {
          justifyContent:
            showPrivacySummary && !isDesktop && !isTablet
              ? 'flex-start'
              : 'inherit',
          position:
            showPrivacySummary && !isDesktop && !isTablet
              ? 'relative'
              : 'inherit'
        }
      }}
    >
      <Button
        variant="primary"
        onClick={() => {
          trackHandler(startSession, 'lets-chat-btn')(true)
        }}
        sx={{ ...styles.letsChatButton, ...sx }}
      >
        {t('app:Home.letsChat')}
      </Button>
    </Flex>
  )

  return (
    <Flex sx={styles.mainContainer}>
      <Flex sx={styles.dhImageContainer}>
        <Image
          src={dhImage}
          sx={{
            ...styles.dhImage,
            filter:
              showPrivacySummary && !isDesktop && !isTablet
                ? 'blur(10px)'
                : 'inherit',
            opacity: showPrivacySummary && !isDesktop && !isTablet ? 0.3 : 1,
            '@media only screen and (orientation: landscape)': {
              filter: 'inherit',
              opacity: 'inherit'
            }
          }}
        />
      </Flex>
      <Flex sx={styles.content}>
        <Flex sx={styles.textContainer}>
          <Box sx={styles.titleContainer}>
            <Heading as="h1" sx={styles.heading}>
              <Trans
                i18nKey={`app:Home.heading`}
                values={{ avatarName: config.avatarName }}
              />
            </Heading>
          </Box>

          <Heading as="h2" sx={styles.subtitle}>
            {t('app:Home.intro')}
          </Heading>

          <LetsChatButton />

          <Paragraphs />
        </Flex>
      </Flex>
    </Flex>
  )
}

export default Home
